class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null;
    this.bindings = new Map();
    this.isInitialized = false;
  }

  static get observedAttributes() {
    return ['date', 'lang'];
  }

  connectedCallback() {
    if (!this.isInitialized) {
      this.initializeComponent();
      this.isInitialized = true;
    }
  }

  disconnectedCallback() {
    this.clearTimer();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Prevent infinite recursion by checking if value actually changed
    if (oldValue === newValue) return;
    
    // Only process if component is initialized
    if (!this.isInitialized) return;

    if (name === 'date') {
      this.handleDateChange(newValue);
    } else if (name === 'lang') {
      this.handleLanguageChange(newValue);
    }
  }

  initializeComponent() {
    this.dateAttr = this.getAttribute('date');
    this.lang = this.getAttribute('lang') || 'ru';
    this.parseDate();
    this.renderFromTemplate();
    this.startCountdown();
  }

  handleDateChange(newValue) {
    this.dateAttr = newValue;
    this.parseDate();
    this.clearTimer();
    this.startCountdown();
  }

  handleLanguageChange(newValue) {
    this.lang = newValue;
    // Update display with current values using new language
    this.updateCurrentDisplay();
  }

  parseDate() {
    if (!this.dateAttr) {
      console.warn('CountdownTimer: No date attribute provided');
      return;
    }

    // Check for everyday format with optional timezone: everyday@23:59:00 PST
    const everydayMatch = this.dateAttr.match(/^everyday(?:@(\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s+([A-Z]{3,4}))?$/);

    if (everydayMatch) {
      this.isEveryday = true;
      this.everydayTime = {
        hours: parseInt(everydayMatch[1] ?? '23', 10),
        minutes: parseInt(everydayMatch[2] ?? '59', 10),
        seconds: parseInt(everydayMatch[3] ?? '59', 10),
      };
      this.everydayTimezone = everydayMatch[4] || null; // Store timezone if provided
      this.targetDate = this.getTodayAt(this.everydayTime, this.everydayTimezone);
    } else {
      this.isEveryday = false;
      this.everydayTimezone = null;
      this.targetDate = this.parseDateString(this.dateAttr);
      
      // Validate the parsed date
      if (isNaN(this.targetDate.getTime())) {
        console.error('CountdownTimer: Invalid date format:', this.dateAttr);
        this.targetDate = new Date(); // Fallback to current date
      }
    }
  }

  parseDateString(dateStr) {
    // Handle various date formats
    const trimmed = dateStr.trim();
    
    // Format: "July 26, 2025 22:59:00 PST" or similar with timezone
    const namedDateWithTzMatch = trimmed.match(/^(\w+)\s+(\d{1,2}),\s+(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s+([A-Z]{3,4})$/);
    if (namedDateWithTzMatch) {
      const [, month, day, year, hour, minute, second = '0', timezone] = namedDateWithTzMatch;
      
      // Convert month name to number
      const monthNum = this.getMonthNumber(month);
      if (monthNum === -1) {
        console.warn('CountdownTimer: Invalid month name:', month);
        return new Date(trimmed); // Fallback to native parsing
      }
      
      // Create date string in ISO format for better parsing
      const isoString = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
      const date = new Date(isoString);
      
      // Apply timezone offset if supported
      return this.applyTimezone(date, timezone);
    }
    
    // Format: "July 26, 2025 22:59:00" (without timezone)
    const namedDateMatch = trimmed.match(/^(\w+)\s+(\d{1,2}),\s+(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (namedDateMatch) {
      const [, month, day, year, hour, minute, second = '0'] = namedDateMatch;
      
      const monthNum = this.getMonthNumber(month);
      if (monthNum === -1) {
        console.warn('CountdownTimer: Invalid month name:', month);
        return new Date(trimmed);
      }
      
      return new Date(year, monthNum - 1, day, hour, minute, second);
    }
    
    // Format: "2025-07-26 22:59:00" (ISO-like)
    const isoLikeMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (isoLikeMatch) {
      const [, year, month, day, hour, minute, second = '0'] = isoLikeMatch;
      return new Date(year, month - 1, day, hour, minute, second);
    }
    
    // Format: "26/07/2025 22:59:00" (European format)
    const europeanMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (europeanMatch) {
      const [, day, month, year, hour, minute, second = '0'] = europeanMatch;
      return new Date(year, month - 1, day, hour, minute, second);
    }
    
    // Format: "07/26/2025 22:59:00" (American format)
    const americanMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (americanMatch) {
      const [, month, day, year, hour, minute, second = '0'] = americanMatch;
      return new Date(year, month - 1, day, hour, minute, second);
    }
    
    // Fallback to native Date parsing
    return new Date(trimmed);
  }

  getMonthNumber(monthName) {
    const months = {
      'january': 1, 'jan': 1,
      'february': 2, 'feb': 2,
      'march': 3, 'mar': 3,
      'april': 4, 'apr': 4,
      'may': 5,
      'june': 6, 'jun': 6,
      'july': 7, 'jul': 7,
      'august': 8, 'aug': 8,
      'september': 9, 'sep': 9, 'sept': 9,
      'october': 10, 'oct': 10,
      'november': 11, 'nov': 11,
      'december': 12, 'dec': 12
    };
    
    return months[monthName.toLowerCase()] || -1;
  }

  applyTimezone(date, timezone) {
    // Basic timezone support for common US timezones
    const timezoneOffsets = {
      'PST': -8, 'PDT': -7,  // Pacific
      'MST': -7, 'MDT': -6,  // Mountain
      'CST': -6, 'CDT': -5,  // Central
      'EST': -5, 'EDT': -4,  // Eastern
      'UTC': 0, 'GMT': 0     // Universal
    };
    
    const offset = timezoneOffsets[timezone.toUpperCase()];
    if (offset !== undefined) {
      // Adjust for timezone offset
      const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
      return new Date(utcTime + (offset * 3600000));
    }
    
    // If timezone not recognized, return date as-is
    console.warn('CountdownTimer: Unrecognized timezone:', timezone);
    return date;
  }

  getTodayAt({ hours, minutes, seconds }, timezone = null) {
    let now = new Date();
    
    // If timezone is specified, convert current time to that timezone
    if (timezone) {
      now = this.convertToTimezone(now, timezone);
    }
    
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, seconds);
    
    // If the time has already passed today, set it for tomorrow
    if (target <= now) {
      target.setDate(target.getDate() + 1);
    }
    
    // If timezone was specified, we need to convert back to local time for comparison
    if (timezone) {
      return this.convertFromTimezone(target, timezone);
    }
    
    return target;
  }

  convertToTimezone(date, timezone) {
    const timezoneOffsets = {
      'PST': -8, 'PDT': -7,  // Pacific
      'MST': -7, 'MDT': -6,  // Mountain  
      'CST': -6, 'CDT': -5,  // Central
      'EST': -5, 'EDT': -4,  // Eastern
      'UTC': 0, 'GMT': 0     // Universal
    };
    
    const offset = timezoneOffsets[timezone.toUpperCase()];
    if (offset === undefined) {
      console.warn('CountdownTimer: Unrecognized timezone for everyday:', timezone);
      return date;
    }
    
    // Convert to the specified timezone
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000);
    return new Date(utcTime + (offset * 3600000));
  }

  convertFromTimezone(date, timezone) {
    const timezoneOffsets = {
      'PST': -8, 'PDT': -7,  // Pacific
      'MST': -7, 'MDT': -6,  // Mountain
      'CST': -6, 'CDT': -5,  // Central
      'EST': -5, 'EDT': -4,  // Eastern
      'UTC': 0, 'GMT': 0     // Universal
    };
    
    const offset = timezoneOffsets[timezone.toUpperCase()];
    if (offset === undefined) {
      return date;
    }
    
    // Convert from the specified timezone back to local time
    const timezoneTime = date.getTime() - (offset * 3600000);
    return new Date(timezoneTime - (date.getTimezoneOffset() * 60000));
  }

  getPluralForm(n, one, few, many) {
    if (this.lang === 'en') {
      return n === 1 ? one : few;
    }
    
    // Russian/Ukrainian plural rules
    const n10 = n % 10;
    const n100 = n % 100;
    
    if (n100 >= 11 && n100 <= 14) return many;
    if (n10 === 1) return one;
    if (n10 >= 2 && n10 <= 4) return few;
    return many;
  }

  parseBinding(binding, values) {
    // Handle different binding formats:
    // 1. Simple: "hours" or "hours pad=2"
    // 2. With plurals: "hours:hour:hours:hours" or "hours:час:часа:часов"
    // 3. With plurals and padding: "hours:hour:hours:hours pad=2"
    
    const regex = /^(\w+)(?::([^:\s]+):([^:\s]+):([^:\s]+))?(?:\s+pad=(\d+))?$/;
    const match = binding.match(regex);
    
    if (!match) {
      console.warn('CountdownTimer: Invalid binding format:', binding);
      return '';
    }

    const [, key, f1, f2, f3, padSize] = match;
    const val = values[key] ?? 0;
    
    // Apply padding if specified
    const padded = padSize ? String(val).padStart(parseInt(padSize, 10), '0') : String(val);

    // If plural forms are provided, use them
    if (f1 && f2 && f3) {
      const pluralForm = this.getPluralForm(val, f1, f2, f3);
      return `${padded} ${pluralForm}`;
    }
    
    // Otherwise just return the padded number
    return padded;
  }

  updateBindings(values) {
    for (const [bind, el] of this.bindings.entries()) {
      if (el && el.isConnected) {
        el.textContent = this.parseBinding(bind, values);
      }
    }
  }

  renderFromTemplate() {
    const tmpl = this.querySelector('template');
    
    if (!tmpl) {
      this.innerHTML = `
        <div style="padding: 10px; text-align: center;">
          ⏳ No template found
        </div>
      `;
      return;
    }

    const clone = tmpl.content.cloneNode(true);
    this.bindings.clear();
    
    // Find all elements with data-bind attribute
    const bindElements = clone.querySelectorAll('[data-bind]');
    
    bindElements.forEach(el => {
      const bindAttr = el.getAttribute('data-bind');
      if (bindAttr) {
        // Clean the binding string - remove curly braces and extra whitespace
        const cleanBind = bindAttr.trim().replace(/^\{|\}$/g, '');
        this.bindings.set(cleanBind, el);
      }
    });

    // Clear existing content except template
    while (this.firstChild && this.firstChild !== tmpl) {
      this.removeChild(this.firstChild);
    }
    while (this.lastChild && this.lastChild !== tmpl) {
      this.removeChild(this.lastChild);
    }
    
    // Insert the rendered content
    this.appendChild(clone);
  }

  calculateTimeValues() {
    const now = new Date();
    let diff = this.targetDate - now;

    if (diff <= 0) {
      if (this.isEveryday) {
        // Reset to next occurrence for everyday timers
        this.targetDate = this.getTodayAt(this.everydayTime, this.everydayTimezone);
        diff = this.targetDate - now;
      } else {
        // Timer has expired
        return { 
          years: 0, 
          days: 0, 
          hours: 0, 
          minutes: 0, 
          seconds: 0, 
          totalHours: 0,
          totalMinutes: 0,
          totalSeconds: 0,
          expired: true 
        };
      }
    }

    let totalSeconds = Math.floor(diff / 1000);
    
    // Calculate total values (without breaking down into larger units)
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalSeconds / 3600);
    
    // Store original totalSeconds for later use
    const allSeconds = totalSeconds;
    
    // Calculate standard breakdown
    const years = Math.floor(totalSeconds / (365.25 * 24 * 3600));
    totalSeconds -= Math.floor(years * 365.25 * 24 * 3600);

    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { 
      years, 
      days, 
      hours, 
      minutes, 
      seconds, 
      totalHours,
      totalMinutes,
      totalSeconds: allSeconds,
      expired: false 
    };
  }

  updateCurrentDisplay() {
    const values = this.calculateTimeValues();
    this.updateBindings(values);
  }

  startCountdown() {
    const tick = () => {
      const values = this.calculateTimeValues();
      
      if (values.expired && !this.isEveryday) {
        this.clearTimer();
        this.dispatchEvent(new CustomEvent('countdown-expired', { detail: values }));
      }
      
      this.updateBindings(values);
      this.dispatchEvent(new CustomEvent('countdown-tick', { detail: values }));
    };

    // Clear any existing timer first
    this.clearTimer();
    
    // Initial tick
    tick();
    
    // Set up interval
    this.timerInterval = setInterval(tick, 1000);
  }

  clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  // Public API methods
  pause() {
    this.clearTimer();
  }

  resume() {
    if (!this.timerInterval) {
      this.startCountdown();
    }
  }

  reset() {
    this.clearTimer();
    this.parseDate();
    this.startCountdown();
  }
}

customElements.define('countdown-timer', CountdownTimer);